import classnames from "classnames";
import includes from "lodash-es/includes";
import React, {
  ReactNode,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Helmet } from "react-helmet";
import { RouteComponentProps } from "react-router-dom";
import { StickyContainer } from "react-sticky";
import { BannersBlock } from "src/components";
import { usePrismicData } from "src/components/Banner/hooks/usePrismicData";
import { IBannerData } from "src/components/Banner/types";
import { Container } from "src/components/Layout";
import { useRenderOn } from "src/hooks";
import styled from "styled-components";

import { IWithCart } from "../../../hoc/repository/withCart/withCart";
import { IWithMe } from "../../../hoc/repository/withMe/withMe";
import analytics from "../../../lib/analytics";
import { IProductAnalytics } from "../../../services/analytics.service";
import elitrackTag, { IElitrackProps } from "../../../services/elitrack";
import { convertNumberToString } from "../../../services/number.service";
import { translate } from "../../../services/translation.service";
import theme, { media } from "../../../theme";
import { ICartProductDetail } from "../../../types/cart";
import { IProduct } from "../../../types/product";

import { Footer, Header, Notify, SubHeader } from "./components";

interface IWrapperPage {
  backgroundColor?: string;
}

const WrapperPage = styled.div<IWrapperPage>`
  display: flex;
  flex-direction: column;
  ${(props: IWrapperPage): string =>
    props.backgroundColor ? `background-color: ${props.backgroundColor}` : ""};
  flex-grow: 1;
  position: relative;
  min-height: 100vh;
`;

interface IBodyWrapper {
  horizontalPadding?: number;
  noPadding: boolean;
}

const BodyWrapper = styled.section<IBodyWrapper>`
  padding: ${(props: IBodyWrapper): string =>
    props.noPadding
      ? "0"
      : props.horizontalPadding !== undefined
      ? `${theme.margins.paddingPage} ${props.horizontalPadding}px`
      : theme.margins.paddingPageBig};
  padding-top: 0;
  flex-grow: 1;

  ${media.mobile} {
    padding-bottom: 0;
  }
`;

const Preheader = styled(BannersBlock)``;

const Undermenu = styled(BannersBlock)`
  &.banner-wrapper-funnel,
  &.banner-wrapper-checkout {
    margin-bottom: ${theme.margins.marginBase};
  }
`;

const Background = styled(BannersBlock)``;

interface IBackgroundColor {
  backgroundColor: string;
}

const ChildrenContainer = styled(Container)<IBackgroundColor>`
  background: ${({ backgroundColor }: IBackgroundColor): string =>
    backgroundColor};
`;

type IString = string | undefined | null;

export interface IProps extends IWithMe, IWithCart, RouteComponentProps {
  analyticsPageId?: string;
  backgroundColor?: string;
  bannerContent?: ReactNode;
  brandId?: string;
  canGoBack?: boolean;
  children: ReactNode[] | ReactNode;
  className?: string;
  description?: string;
  elitrackProps?: IElitrackProps;
  hamburgerMenu?: boolean;
  childrenInFullWidth?: boolean;
  headerRightIcon?: ReactNode;
  headerSibling?: ReactNode;
  horizontalPadding?: number;
  id?: string;
  isHeaderSticky?: boolean;
  isOrderConfirmationPage?: boolean;
  noFooter?: boolean;
  noHeader?: boolean;
  noPadding?: boolean;
  noUnderMenu?: boolean;
  hasBackground?: boolean;
  pageType?: string;
  preventStickyInFooter?: boolean;
  product?: IProduct;
  subTitle?: string;
  tabTitle?: string | null;
  title?: string;
}

const Page = ({
  analyticsPageId,
  backgroundColor = theme.colors.white,
  bannerContent,
  brandId,
  canGoBack,
  cart,
  cartLoading,
  children,
  className,
  description = translate("global.description"),
  elitrackProps,
  hamburgerMenu,
  childrenInFullWidth = false,
  headerRightIcon,
  headerSibling,
  horizontalPadding,
  id,
  isHeaderSticky,
  isOrderConfirmationPage = false,
  me,
  meLoading,
  noFooter = false,
  noHeader = false,
  noPadding = false,
  noUnderMenu,
  hasBackground = false,
  pageType,
  preventStickyInFooter,
  product,
  subTitle,
  tabTitle,
  title,
}: IProps): JSX.Element => {
  const { lgOrUp, ...renderOnProps } = useRenderOn();

  /**
   * States
   */
  const [hasElitrackTag, setHasElitrackTag] = useState<boolean>(false);
  const [prevAnalyticsPageId, setPrevAnalyticsPageId] = useState<IString>(null);
  const [prevUrl, setPrevUrl] = useState<IString>(null);

  /**
   * Checkout page
   */

  const onCheckoutPage = useCallback((): void => {
    const productsForAnalytics: IProductAnalytics[] = [];
    cart?.items.forEach((item: ICartProductDetail) => {
      productsForAnalytics.push({
        brand: item.product.brand?.name,
        category: item.product.category,
        dimension1: item.product.productFamily,
        dimension2: item.product.productSubFamily,
        dimension8: convertNumberToString(item.product.originalPrice),
        ean: item.product?.ean,
        id: (item.product.id || "").split("-")[0],
        name: item.product.name,
        price: convertNumberToString(item.product.actualPrice),
        quantity: item.quantity,
        sku: item.product.sku,
        variant: item.product.volume,
      });
    });

    analytics.push({
      ecommerce: {
        products: productsForAnalytics,
      },
      event: "checkout",
      step: analyticsPageId,
    });
  }, [analyticsPageId, cart]);

  /**
   * Elitrack
   */
  const handleElitrack = useCallback((): void => {
    if (!hasElitrackTag && pageType && !cartLoading && !meLoading) {
      setHasElitrackTag(true);

      const a = document.createElement("script");
      a.type = "text/javascript";
      a.charset = "UTF-8";
      a.async = true;
      a.defer = true;
      a.src = elitrackTag(pageType, elitrackProps, me?.email);

      document.getElementsByTagName("head")[0].appendChild(a);
    }
  }, [cartLoading, elitrackProps, hasElitrackTag, me, meLoading, pageType]);

  /**
   * Lifecycles
   */
  // PrevUrl
  useEffect(() => {
    // Reset api __initialUrlState once a component is loaded
    window.__initialUrlState = undefined;

    const currentUrl = window.location.pathname;

    if (currentUrl !== prevUrl) {
      window.scrollTo(0, 0);

      setPrevUrl(currentUrl);
    }
  }, [prevUrl]);

  // Elitrack
  useEffect(() => {
    handleElitrack();
  }, [handleElitrack]);

  // Checkout
  useEffect(() => {
    if (
      includes(
        ["cart", "connexion", "shipping", "payment", "confirmation"],
        analyticsPageId
      ) &&
      analyticsPageId !== prevAnalyticsPageId &&
      !cartLoading
    ) {
      onCheckoutPage();
      setPrevAnalyticsPageId(analyticsPageId);
    }
  }, [analyticsPageId, cartLoading, onCheckoutPage, prevAnalyticsPageId]);

  const showTitle = title != null && title.length > 0;

  /**
   * Fetch prismic banner data
   */
  const prismicData = usePrismicData();
  const backgroundData: IBannerData[] | undefined = useMemo(
    () => prismicData?.data.body4,
    [prismicData]
  );

  const showBackground = (): boolean | void => {
    if (backgroundData) {
      const background = backgroundData.find(
        (item) => item.primary?.["background_on_" + pageType]
      );

      return hasBackground && background && lgOrUp;
    }
  };

  return (
    <StickyContainer>
      <Helmet>
        <title>
          {tabTitle ? tabTitle + " - " : ""}
          {translate("tabTitle.base")}
        </title>
        <meta name="description" content={description} />
      </Helmet>

      <WrapperPage
        backgroundColor={backgroundColor}
        className={classnames(className, "page")}
        id={id}
      >
        <Notify isOrderConfirmationPage={isOrderConfirmationPage!} />

        <Preheader
          template="preheader"
          pageType={pageType}
          brandId={brandId}
          bannerData={prismicData}
        />

        {!noHeader && (
          <Suspense fallback={<div />}>
            <Header
              isSticky={isHeaderSticky}
              pageType={pageType}
              brandId={brandId}
              productId={product && product.id}
              productSku={product && product.sku}
              noUnderMenu={noUnderMenu}
              renderOnProps={{ lgOrUp, ...renderOnProps }}
            />
          </Suspense>
        )}

        {showTitle && (
          <Suspense fallback={<div />}>
            <SubHeader
              canGoBack={canGoBack}
              hamburgerMenu={hamburgerMenu}
              headerRightIcon={headerRightIcon}
              title={title}
              subTitle={subTitle}
              headerSibling={headerSibling}
              pageType={pageType}
              brandId={brandId}
            />
          </Suspense>
        )}

        {!noUnderMenu && (
          <Undermenu
            template="underMenu"
            pageType={pageType}
            brandId={brandId}
            bannerData={prismicData}
          />
        )}
        <BodyWrapper
          horizontalPadding={horizontalPadding}
          noPadding={noPadding || false}
          className="page-content"
        >
          {showBackground() ? (
            <Background
              template="background"
              pageType={pageType}
              brandId={brandId}
              bannerData={prismicData}
            >
              <ChildrenContainer
                noGutter
                fullWidth="lg"
                backgroundColor={backgroundColor}
              >
                {children}
              </ChildrenContainer>
            </Background>
          ) : childrenInFullWidth ? (
            children
          ) : (
            <ChildrenContainer
              noGutter
              fullWidth="lg"
              backgroundColor={backgroundColor}
            >
              {children}
            </ChildrenContainer>
          )}
        </BodyWrapper>
        {bannerContent}

        {!noFooter && (
          <Suspense fallback={<div />}>
            <Footer preventStickyInFooter={preventStickyInFooter} />
          </Suspense>
        )}
      </WrapperPage>
    </StickyContainer>
  );
};

export default Page;
